/**
 * Auto-selects the best registered brand-logo variation for a given style
 * background tone, so generated banners keep the logo visible instead of
 * asking the AI to recolor it.
 *
 * Rule: background_tone='light' の場合 logo_tone='light' のロゴは見えにくい
 * → 'dark' を優先、次点 'color'。background_tone='dark' の場合はその逆
 * （'light' 優先、次点 'color'）。適切な代替が無い場合は selectLogoForBackground が null を返す。
 */

export type LogoTone = 'light' | 'dark' | 'color';
export type BackgroundTone = 'light' | 'dark';

/**
 * Minimal shape required to pick among logo candidates. Both ProductImage
 * variants in this codebase (services/api/productImageService.ts and
 * types/knowledge.ts) satisfy this structurally, so callers can pass either
 * without conversion.
 */
export interface LogoToneCandidate {
  logo_tone?: LogoTone | null;
  is_primary: boolean;
  sort_order: number;
}

/**
 * Picks the best-matching logo variation for a given background tone.
 * Within the chosen tone, is_primary wins, then lowest sort_order.
 * Returns null when no suitable alternate (preferred tone or color) is registered.
 */
export function selectLogoForBackground<T extends LogoToneCandidate>(
  logos: T[],
  backgroundTone: BackgroundTone,
): T | null {
  if (!logos || logos.length === 0) return null;

  const preferredTone: LogoTone = backgroundTone === 'light' ? 'dark' : 'light';

  const pickBestByTone = (tone: LogoTone): T | null => {
    const matches = logos.filter((logo) => logo.logo_tone === tone);
    if (matches.length === 0) return null;
    return [...matches].sort((a, b) => {
      if (a.is_primary !== b.is_primary) return a.is_primary ? -1 : 1;
      return a.sort_order - b.sort_order;
    })[0];
  };

  return pickBestByTone(preferredTone) ?? pickBestByTone('color') ?? null;
}

/**
 * A logo conflicts with a background when both share the same tone
 * (light logo on a light background, dark logo on a dark background).
 * Unknown tones (color, or missing/unclassified data) never conflict —
 * we only ever swap when we're confident visibility is actually a problem.
 */
export function isLogoToneConflicting(
  logoTone: LogoTone | null | undefined,
  backgroundTone: BackgroundTone | null | undefined,
): boolean {
  if (!logoTone || !backgroundTone) return false;
  return logoTone === backgroundTone;
}
